<template>
    <div class="expense-form">
        <form
            action=""
            @submit.prevent="save"
            class="form"
        >
            <div class="row">
                <div class="col-12">
                    <div class="form-group">
                        <label for="expense_date">Expense Date:</label>
                        <date-picker
                            v-model="form.expense_date"
                            id="expense_date"
                            :readonly="!canEdit"
                            :options="{format: 'L'}"
                        />
                        <error
                            :errors="errors"
                            name="expense_date"
                        />
                    </div>
                </div>
            </div>
            <div class="row">
                <div class="col-12">
                    <div class="form-group">
                        <vehicle-select
                            v-model="form.vehicle_reg"
                            label="Vehicle Reg (optional):"
                            optional
                            :readonly="!canEdit"
                            :errors="errors"
                        />
                    </div>
                </div>
            </div>

            <div class="row">
                <div class="col-12">
                    <div class="form-group">
                        <label for="notes">Amount:</label>
                        <div class="input-group">
                            <div class="input-group-prepend">
                                <span class="input-group-text">£</span>
                            </div>
                            <input
                                type="number"
                                min="0.01"
                                step="0.01"
                                max="1000"
                                class="form-control"
                                v-model="form.expense_amount"
                                :readonly="!canEdit"
                            >
                        </div>
                        <error
                            :errors="errors"
                            name="expense_amount"
                        />
                    </div>
                </div>
            </div>

            <div class="row">
                <div class="col-12">
                    <div class="form-group">
                        <label for="notes">Notes:</label>
                        <textarea
                            v-model="form.notes"
                            id="notes"
                            :readonly="!canEdit"
                            rows="3"
                            class="form-control"
                            placeholder="Describe what you expense claim is for here..."
                        />
                        <error
                            :errors="errors"
                            name="notes"
                        />
                    </div>
                </div>
            </div>

            <div class="row">
                <div class="col-12">
                    <attachments
                        type="App\EmployeeExpense"
                        :id="form.id"
                        @input="attachmentsChanged"
                        :view-only="!canEdit"
                    />
                </div>
            </div>
            <br>
            <div
                class="row"
                v-if="form.status"
            >
                <div class="col-12">
                    <div class="form-group">
                        <label>Status:</label>
                        <span
                            class="expense-row-status"
                            :class="form.status.toLowerCase()"
                        >{{ form.status }}</span>
                    </div>
                </div>
            </div>

            <div
                class="row"
                v-if="form.reviewer"
            >
                <div class="col-12">
                    <div class="form-group">
                        <label>Reviewed By:</label>
                        <span>{{ form.reviewer.name }}</span>
                    </div>
                </div>
            </div>

            <div
                class="row"
                v-if="form.payment_amount"
            >
                <div class="col-12">
                    <div class="form-group">
                        <label>Payment Amount:</label>
                        <span>£{{ form.payment_amount | money }}</span>
                    </div>
                </div>
            </div>

            <div
                class="row"
                v-if="form.payment_method"
            >
                <div class="col-12">
                    <div class="form-group">
                        <label>Payment Method:</label>
                        <span>£{{ form.payment_method }}</span>
                    </div>
                </div>
            </div>

            <div class="row">
                <div class="col-12">
                    <button
                        class="btn btn-primary"
                        v-if="isNew"
                        :disabled="!canEdit"
                    >
                        Submit Expense
                    </button>
                    <button
                        class="btn btn-primary"
                        v-if="!isNew"
                        :disabled="!canEdit"
                    >
                        Update Expense
                    </button>
                    <button
                        class="btn btn-danger"
                        v-if="form.id"
                        :disabled="!canEdit"
                        @click.prevent="deleteExpense"
                    >
                        Delete Expense
                    </button>
                    <router-link
                        class="btn btn-default"
                        :to="{name: 'engineer.expenses.index'}"
                    >
                        Back
                    </router-link>
                </div>
            </div>
        </form>
    </div>
</template>

<script>
import DatePicker from '../DateTimePickerComponent';
import Service from '../../services/EmployeeExpensesService';
import VehicleSelect from '../VehicleSelectComponent.vue';
import UsesInfoMessage from '../../mixins/UsesInfoMessage';
import UsesErrorMessage from '../../mixins/UsesErrors';
import Attachments from '../AttachmentsComponent';
export default {
    components: {
        DatePicker,
        VehicleSelect,
        Attachments
    },
    mixins: [
        UsesInfoMessage,
        UsesErrorMessage,
    ],
    data () {
        return {
            form: {},
            saving: false,
            loading: false,
        };
    },
    computed: {
        canEdit () {
            return (this.form.status === 'Pending' || !this.form.id) && !this.saving;
        },

        isNew () {
            return typeof this.form.id ===  'undefined';
        }
    },
    watch: {
        $route: {
            immediate: true,
            handler () {
                this.load();
            }
        }
    },
    methods: {
        save () {
            this.clearErrors(),
            this.saving = true;
            Service.save(this.form).then(res => {
                this.setInfoMessage('Expense saved');
                this.saving = false;
                if (this.isNew) {
                    this.$router.push({name: 'engineer.expenses.edit', params: {id: res.data.id}});
                    return;
                }
                this.form = res.data;
            }).catch(err => {
                this.saving = false;
                this.captureRequestErrors(err);
            })
        },
        load () {
            this.loading = true;
            if (!this.$route.params.id) {
                this.reset();
                this.loading = false;
                return;
            }
            Service.get(this.$route.params.id).then(res => {
                this.form = {
                    ...res.data,
                    expense_date: window.moment(res.data.expense_date)
                };
                this.loading = false;
            }).catch(err => {
                console.error(err);
                this.setErrorMessage('Failed to load expense');
                this.gotoIndex();
            });
        },
        reset () {
            this.form = {
                expense_date: window.moment(),
                vehicle_reg: null,
                expense_amount: 0.01,
            };
        },
        deleteExpense () {
            const confirm = window.confirm('Are you sure you want to delete this expense?');
            if (!confirm) {
                return;
            }
            this.saving = true;
            Service.delete(this.form.id).then(() => {
                this.setInfoMessage('Expense deleted successfully');
                this.gotoIndex();
            }).catch(err => {
                console.error(err);
                this.saving = false;
                this.setErrorMessage('Failed to delete expense');
            })
        },
        gotoIndex () {
            this.$router.replace({name: 'engineer.expense.index'});
        },
        attachmentsChanged (payload) {
            this.form.attachments = payload;
        }
    }
}
</script>


// WEBPACK FOOTER //
// resources/assets/js/components/engineer/ExpensesForm.vue